import React, { useEffect, useMemo } from 'react';
import { UIGameActionButtonSettlement } from './UIGameActionButtonSettlement/index.ts';
import clsx from 'clsx';
import styles from './UIGameActionButtonContainer.module.scss';
import { UIGameActionButtonRoad } from './UIGameActionButtonRoad/index.ts';
import { UIGameActionButtonCity } from './UIGameActionButtonCity/index.ts';
import { UIGameActionButtonTurn } from './UIGameActionButtonTurn/index.ts';
import { UIGameActionButtonBuyDevelopmentCard } from './UIGameActionButtonBuyDevelopmentCard/index.ts';
import { UIGameActionButtonTrade } from './UIGameActionButtonTrade/index.ts';
import { UIGameActionButtonShip } from './UIGameActionButtonShip/index.ts';
import { UIGameActionButtonShipMove } from './UIGameActionButtonShipMove/index.ts';
import { UIGameActionButtonSpecialBuildPhase } from './UIGameActionButtonSpecialBuildPhase/index.ts';
import { UIGameActionButtonCityWall } from './UIGameActionButtonCityWall/index.ts';
import { UIGameActionButtonCityImprovements } from './UIGameActionButtonCityImprovements/index.ts';
import { UIGameActionButtonKnightActions } from './UIGameActionButtonKnightActions/index.ts';
import { useGameStore } from '../../store/GameStoreContext.ts';
import { UIGameTimerContainer } from '../UIGameTimer/UIGameTimerContainer.tsx';
import { UIGameCurrentStatusContainer } from '../UIGameCurrentStatus/index.ts';
import { ModeSettingEnum } from '@colonist/types';
import { useIsReplay } from '../../store/selectors/useIsReplay.ts';
import { useCurrentStateValidator } from '../../store/selectors/useCurrentStateValidator.ts';
function useCloseAllExtraButtons() {
    const currentStateValidator = useCurrentStateValidator();
    const myColor = useGameStore((state)=>state.myColor);
    const closeAllExtraButtons = useGameStore((state)=>state.gameActionButtons.closeAllExtraButtons);
    const isShowTradePanel = useGameStore((state)=>state.gameTrade.isShowTradePanel);
    const hasActionBoxOpening = useGameStore((state)=>state.actionBox.actionBoxData != null);
    const isMyTurn = currentStateValidator.currentTurnPlayers.includes(myColor);
    const hasPopupOpening = hasActionBoxOpening || isShowTradePanel;
    useEffect(()=>{
        if (!isMyTurn) closeAllExtraButtons();
    }, [
        isMyTurn
    ]);
    useEffect(()=>{
        if (hasPopupOpening) closeAllExtraButtons();
    }, [
        hasPopupOpening
    ]);
}
const UIGameActionButtonContainerLandscape = ({ className })=>{
    const isReplay = useIsReplay();
    const isShowTradePanel = useGameStore((state)=>state.gameTrade.isShowTradePanel);
    const gameSettings = useGameStore((state)=>state.gameSettings);
    const hasCityWallButton = useGameStore((state)=>state.gameState.mechanicCityWallState != null);
    const hasShipButtons = useGameStore((state)=>state.gameState.mechanicShipState != null);
    const hasSpecialBuildPhaseButton = useGameStore((state)=>state.gameState.mechanicSpecialBuildPhaseState != null);
    const hasBuyDevelopmentCardButton = useGameStore((state)=>state.gameState.mechanicDevelopmentCardsState != null);
    const hasCityImprovementButtons = useGameStore((state)=>state.gameState.mechanicCityImprovementsState != null);
    const hasBothShipAndCityImprovementButtons = hasCityImprovementButtons && hasShipButtons;
    const isMyColorSpectator = useGameStore((state)=>state.isMyColorSpectator);
    const hasActionBoxOpening = useGameStore((state)=>state.actionBox.actionBoxData != null);
    const hasPopupOpening = hasActionBoxOpening || isShowTradePanel;
    const isModeWithSpecialBuildPhaseButtonRepositioned = useMemo(()=>[
            ModeSettingEnum.Seafarers56P,
            ModeSettingEnum.CitiesAndKnights56P,
            ModeSettingEnum.CitiesAndKnightsSeafarers56P
        ].includes(gameSettings.modeSetting), [
        gameSettings.modeSetting
    ]);
    useCloseAllExtraButtons();
    if (isMyColorSpectator) {
        return /*#__PURE__*/ React.createElement("div", {
            className: clsx(className, styles.containerLandscape, styles.spectator)
        }, /*#__PURE__*/ React.createElement(UIGameCurrentStatusContainer, {
            className: styles.currentStatus
        }), /*#__PURE__*/ React.createElement(UIGameTimerContainer, {
            className: styles.timer
        }));
    }
    if (isReplay) {
        return /*#__PURE__*/ React.createElement("div", {
            className: clsx(className, styles.containerLandscape, styles.replay)
        }, /*#__PURE__*/ React.createElement(UIGameCurrentStatusContainer, {
            className: styles.currentStatus
        }));
    }
    const offsetForTradeUI = hasBothShipAndCityImprovementButtons ? '-9.5em' : '-0.5em';
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(className, styles.containerLandscape, {
            [styles.hideButtonsForTradeUI]: hasPopupOpening
        }),
        style: {
            marginLeft: offsetForTradeUI,
            gridTemplateColumns: hasBothShipAndCityImprovementButtons ? 'repeat(8, 4.5em)' : '0 0 repeat(6, 4.5em)'
        }
    }, hasCityImprovementButtons && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: styles.knightActionsButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonKnightActions, {
        className: styles.actionButton
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.cityImprovementsButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonCityImprovements, {
        className: styles.actionButton
    }))), hasShipButtons && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: styles.shipButton,
        style: {
            gridColumn: hasBothShipAndCityImprovementButtons ? '1' : '3'
        }
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonShip, {
        className: styles.actionButton
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.shipMoveButton,
        style: {
            gridColumn: hasBothShipAndCityImprovementButtons ? '2' : '4'
        }
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonShipMove, {
        className: styles.actionButton
    }))), hasSpecialBuildPhaseButton && !isModeWithSpecialBuildPhaseButtonRepositioned && /*#__PURE__*/ React.createElement("div", {
        className: styles.specialBuildPhaseButtonContainer
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonSpecialBuildPhase, {
        className: styles.specialBuildPhaseButton
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.currentStatusContainer
    }, hasSpecialBuildPhaseButton && isModeWithSpecialBuildPhaseButtonRepositioned && /*#__PURE__*/ React.createElement(UIGameActionButtonSpecialBuildPhase, {
        className: styles.specialBuildPhaseButton
    }), /*#__PURE__*/ React.createElement(UIGameCurrentStatusContainer, {
        className: styles.currentStatus
    })), /*#__PURE__*/ React.createElement(UIGameTimerContainer, {
        className: styles.timer
    }), /*#__PURE__*/ React.createElement("div", {
        className: styles.tradeButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonTrade, {
        className: styles.actionButton
    })), hasBuyDevelopmentCardButton && /*#__PURE__*/ React.createElement("div", {
        className: styles.buyDevelopmentCardButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonBuyDevelopmentCard, {
        className: styles.actionButton
    })), hasCityWallButton && /*#__PURE__*/ React.createElement("div", {
        className: styles.cityWallButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonCityWall, {
        className: styles.actionButton
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.roadButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonRoad, {
        className: styles.actionButton
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.settlementButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonSettlement, {
        className: styles.actionButton
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.cityButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonCity, {
        className: styles.actionButton
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.turnButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonTurn, {
        className: styles.actionButton
    })));
};
const UIGameActionButtonContainerPotrait = ({ className })=>{
    const currentStateValidator = useCurrentStateValidator();
    const hasCityImprovementButtons = useGameStore((state)=>state.gameState.mechanicCityImprovementsState != null);
    const hasBuyDevelopmentCardButton = useGameStore((state)=>state.gameState.mechanicDevelopmentCardsState != null);
    const isShowTradePanel = useGameStore((state)=>state.gameTrade.isShowTradePanel);
    const hasSpecialBuildPhaseButton = useGameStore((state)=>state.gameState.mechanicSpecialBuildPhaseState != null);
    const myColor = useGameStore((state)=>state.myColor);
    const isMyTurn = currentStateValidator.currentTurnPlayers.includes(myColor);
    const isReplay = useIsReplay();
    useCloseAllExtraButtons();
    if (isShowTradePanel || isReplay) {
        return null;
    }
    if (!isMyTurn) {
        if (hasSpecialBuildPhaseButton) {
            return /*#__PURE__*/ React.createElement("div", {
                className: clsx(className, styles.containerPotrait)
            }, /*#__PURE__*/ React.createElement(UIGameActionButtonSpecialBuildPhase, {
                className: styles.actionButton,
                hideTooltip: true
            }));
        }
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(className, styles.containerPotrait)
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonTrade, {
        className: styles.actionButton,
        hideTooltip: true
    }), hasBuyDevelopmentCardButton && /*#__PURE__*/ React.createElement("div", {
        className: styles.buyDevelopmentCardButton
    }, /*#__PURE__*/ React.createElement(UIGameActionButtonBuyDevelopmentCard, {
        className: styles.actionButton,
        hideTooltip: true
    })), hasCityImprovementButtons && /*#__PURE__*/ React.createElement(UIGameActionButtonCityImprovements, {
        className: styles.actionButton,
        hideTooltip: true
    }), /*#__PURE__*/ React.createElement(UIGameActionButtonTurn, {
        className: styles.actionButton,
        hideTooltip: true
    }));
};
export const UIGameActionButtonContainer = ({ isPortraitLayout, className })=>{
    if (isPortraitLayout) {
        return /*#__PURE__*/ React.createElement(UIGameActionButtonContainerPotrait, {
            className: className
        });
    }
    return /*#__PURE__*/ React.createElement(UIGameActionButtonContainerLandscape, {
        className: className
    });
};
